import { Text, VStack } from '@chakra-ui/react';
import { FileUploadService } from 'core';
import { T_WA_UP_INCIDENT_LIST_PAGE_V2_INCIDENT_RESOLVED } from 'core/const/tracker';
import { FileUploadFeature, OperationType } from 'core/services/service.fileUpload';
import { FormButton, useToast } from 'library/atoms';
import { useCreateEntityMap, useGetOnCallUserSet } from 'library/atoms/MdBlock/helper';
import MdBlock from 'library/atoms/MdBlock/MdBlock';
import { Modal } from 'library/molecules';
import { useMemo, useState } from 'react';
import { AppTracker } from 'shared/analytics/tracker';

import { useSession } from '../behaviors/Session';
import { bulkResolveIncident, resolveIncident } from '../common/commands';
import { INCIDENT_ACTION_ID } from '../common/enums';
import { IResolutionReason } from '../common/interfaces';
import { IncidentResolveProps } from '../common/types';
import { truncate } from '../common/util';

export const ResolveIncident = ({
  incidentIds,
  incidentName = '',
  action,
  onSave,
  onClose,
  containsParentIncidents,
  isBulkResolve = false,
}: IncidentResolveProps) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState('');
  const fileUploadService = new FileUploadService();
  const entities = useCreateEntityMap();

  const {
    session: { orgState: organization, plan },
  } = useSession();

  const { isResolutionReasonEnabled } = useMemo(() => {
    return {
      isResolutionReasonEnabled: plan.rules['incident-resolution-reason'],
    };
  }, [plan]);

  const isResolutionReasonMandatory =
    (isResolutionReasonEnabled &&
      organization.currentOrg.o?.featureSettings.is_resolution_reason_mandatory) ??
    false;

  const onResolve = () => {
    setIsLoading(true);
    bulkResolve();
  };

  const bulkResolve = () => {
    const attachments = fileUploadService.accessAttachmentArrayForIncident();
    const resolutionReason: IResolutionReason = {
      message: content,
      attachments,
    };

    (isBulkResolve
      ? bulkResolveIncident(incidentIds, resolutionReason)
      : resolveIncident(incidentIds[0], resolutionReason)
    )
      .then(() => {
        onSuccess();
      })
      .catch(error => {
        onError(error);
      });
  };

  const onSuccess = () => {
    setIsLoading(false);
    const msgSubStr = isBulkResolve
      ? `${incidentIds.length} incidents`
      : `'${truncate(incidentName)}'`;
    toast({
      status: 'success',
      text: `Success: ${msgSubStr} marked as Resolved`,
    });
    onSave();
    onClose();

    AppTracker.track(T_WA_UP_INCIDENT_LIST_PAGE_V2_INCIDENT_RESOLVED, {
      'Incident ID': incidentIds,
      'Has Resolution Reason': content?.length ? true : false,
    });
  };

  const onError = (error: any) => {
    setIsLoading(false);
    toast({
      status: 'error',
      text: 'Error: Unable to update Incident status',
    });
    console.error(error);
  };

  const onCallUsers = useGetOnCallUserSet();

  return (
    <Modal
      key="resolve-incident"
      size="xl"
      closeOnOverlayClick={false}
      body={
        <VStack rowGap={1} alignItems="flex-start">
          <Text variant="formInput" color="secondary.700">
            {`Are you sure you want to mark ${
              isBulkResolve
                ? `${incidentIds.length} incident${incidentIds.length > 1 ? 's' : ''}`
                : 'this incident'
            } as resolved? ${containsParentIncidents ? 'Some incidents have child incidents' : ''}`}
          </Text>
          {isResolutionReasonEnabled && (
            <>
              <Text variant="formInput_800" color="secondary.700">
                Resolution reason ({isResolutionReasonMandatory ? 'mandatory' : 'optional'})
              </Text>
              <MdBlock
                entities={entities}
                sendMessage={onResolve}
                onMessageChange={setContent}
                placeHolder={`Type reason`}
                onCallUsers={onCallUsers}
                position="bottom"
                hideSendButton={true}
                fileUploadService={fileUploadService}
                featureName={FileUploadFeature.INCIDENT}
                operationType={OperationType.CREATE}
              />
            </>
          )}
          <Text variant="formInput_800" color="secondary.700">
            Note: This action cannot be reversed.
          </Text>
        </VStack>
      }
      isOpen={[INCIDENT_ACTION_ID.INLINE_RESOLVE, INCIDENT_ACTION_ID.BULK_RESOLVE].includes(action)}
      onClose={onClose}
      primaryButton={
        <FormButton
          title="Yes, Resolve"
          onClick={onResolve}
          isLoading={isLoading}
          variant="primary"
          isDisabled={isResolutionReasonMandatory && !content}
        />
      }
      secondaryButton={
        <FormButton title="Cancel" onClick={onClose} disabled={isLoading} variant="secondary" />
      }
      title={`Resolve Incident${isBulkResolve ? 's' : ''}`}
    />
  );
};
